import { IonSpinner } from '@ionic/react';
import React, { useEffect, useRef, useState } from 'react';

import './PdfViewer.scss';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const pdfjsLib = require('pdfjs-dist/legacy/build/pdf');

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

type PdfViewerProps = {
  url: string;
};

const PdfViewer: React.FC<PdfViewerProps> = ({ url }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const renderPdf = async () => {
      // Esperar a que el ref esté disponible
      if (!containerRef.current) {
        await new Promise((resolve) => setTimeout(resolve, 10));
        if (!containerRef.current) return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Limpiar contenedor
        containerRef.current.innerHTML = '';

        // Cargar PDF
        let pdfData;
        if (url.startsWith('blob:')) {
          const response = await fetch(url);
          const arrayBuffer = await response.arrayBuffer();
          pdfData = { data: new Uint8Array(arrayBuffer) };
        } else {
          pdfData = { url };
        }

        const loadingTask = pdfjsLib.getDocument(pdfData);
        const pdf = await loadingTask.promise;

        if (cancelled) return;

        // Renderizar todas las páginas
        const numPages = pdf.numPages;

        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
          if (cancelled) return;

          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1.5 });

          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d', { willReadFrequently: true });

          if (!context) continue;

          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.className = 'pdf-viewer__page';

          await page.render({ canvasContext: context, viewport }).promise;

          if (cancelled) return;

          containerRef.current?.appendChild(canvas);
        }

        setIsLoading(false);
      } catch {
        if (cancelled) return;
        setError('Error al cargar el PDF');
        setIsLoading(false);
      }
    };

    renderPdf();

    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <div className="pdf-viewer-wrapper">
      <div ref={containerRef} className="pdf-viewer" />
      {isLoading && (
        <div className="pdf-viewer__loading-overlay">
          <IonSpinner color="light" />
          <span>Cargando PDF...</span>
        </div>
      )}
      {error && (
        <div className="pdf-viewer__error-overlay">
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default React.memo(PdfViewer);
